import React, { useEffect, useState, useCallback } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { useCookies } from 'react-cookie'
import { getSupportContactSetting, updateSupportContactSetting } from '../../../controller/api/setting'

const DEFAULT_CONFIG = {
  zaloLink: '',
  facebookLink: '',
  hotline: '',
}

const SupportContactSettings = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const hydrateConfig = useCallback((incoming) => {
    const next = {
      ...DEFAULT_CONFIG,
      ...(incoming || {}),
    }
    setConfig(next)
  }, [])

  const fetchSetting = useCallback(async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }
    setLoading(true)
    try {
      const data = await getSupportContactSetting(token)
      hydrateConfig(data)
    } catch (error) {
      console.error('Fetch Support Contact setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể tải cấu hình liên hệ hỗ trợ. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }, [hydrateConfig, token])

  useEffect(() => {
    fetchSetting()
  }, [fetchSetting])

  const handleInputChange = (field) => (event) => {
    setConfig((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }

    setSaving(true)
    try {
      const payload = {
        zaloLink: config.zaloLink,
        facebookLink: config.facebookLink,
        hotline: config.hotline,
      }

      const updated = await updateSupportContactSetting(token, payload)
      hydrateConfig(updated)
      toast.success('Đã lưu cấu hình liên hệ hỗ trợ thành công!')
    } catch (error) {
      console.error('Update Support Contact setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể lưu cấu hình liên hệ hỗ trợ. Vui lòng thử lại.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">📞 Cấu hình Liên hệ Hỗ trợ</h4>
                <small className="text-muted">
                  Thiết lập thông tin liên hệ hỗ trợ khách hàng (Zalo, Facebook và Hotline)
                </small>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={fetchSetting} disabled={loading || saving}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Tải lại'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="zaloLink">
                  <Form.Label>Link Zalo</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://zalo.me/your_zalo_id hoặc https://chat.zalo.me/..."
                    value={config.zaloLink}
                    onChange={handleInputChange('zaloLink')}
                    disabled={loading || saving}
                  />
                  <Form.Text className="text-muted">
                    Nhập link Zalo để khách hàng có thể liên hệ trực tiếp (ví dụ: https://zalo.me/0123456789)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="facebookLink">
                  <Form.Label>Link Facebook</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://facebook.com/your_page hoặc https://m.me/..."
                    value={config.facebookLink}
                    onChange={handleInputChange('facebookLink')}
                    disabled={loading || saving}
                  />
                  <Form.Text className="text-muted">
                    Nhập link Facebook / Messenger để khách hàng có thể liên hệ trực tiếp (ví dụ: https://m.me/veso79)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="hotline">
                  <Form.Label>Hotline</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="0123456789"
                    value={config.hotline}
                    onChange={handleInputChange('hotline')}
                    disabled={loading || saving}
                  />
                  <Form.Text className="text-muted">
                    Số điện thoại hotline hỗ trợ khách hàng
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : 'Lưu cấu hình'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default SupportContactSettings
